import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  BookOpen, ClipboardCheck, Award, MessageSquare, Shield, 
  AlertTriangle, CheckCircle, Star, Target, FileText
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scoring Infographic Component
function ScoringInfographic({ config }) {
  const componentIcons = {
    mcq: BookOpen,
    case_log: ClipboardCheck,
    osce: Award,
    viva: MessageSquare,
    ethics: Shield
  };

  const componentColors = {
    mcq: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    case_log: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    osce: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    viva: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    ethics: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' }
  };

  const componentNames = {
    mcq: 'Written Examination (MCQ)',
    case_log: 'Case Log / Portfolio Review',
    osce: 'OSCE (Clinical Skills)',
    viva: 'Viva Voce & Case Defense',
    ethics: 'Ethics & Governance'
  };

  if (!config) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">No Examination Configuration Selected</h3>
        <p className="text-slate-500 text-sm">Please select an examination from the dropdown above to view scoring details.</p>
      </div>
    );
  }

  if (!config.components || config.components.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">Scoring Configuration</h3>
        <p className="text-slate-500 text-sm mb-4">Detailed scoring breakdown for this examination.</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-left max-w-4xl mx-auto">
          <div className="bg-blue-50 p-4 rounded-sm">
            <BookOpen className="h-6 w-6 text-blue-600 mb-2" />
            <p className="font-semibold text-[#1B2A4A]">Written Exam</p>
            <p className="text-sm text-slate-600">35% weightage</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-sm">
            <ClipboardCheck className="h-6 w-6 text-purple-600 mb-2" />
            <p className="font-semibold text-[#1B2A4A]">Case Log</p>
            <p className="text-sm text-slate-600">20% weightage</p>
          </div>
          <div className="bg-green-50 p-4 rounded-sm">
            <Award className="h-6 w-6 text-green-600 mb-2" />
            <p className="font-semibold text-[#1B2A4A]">OSCE</p>
            <p className="text-sm text-slate-600">25% weightage</p>
          </div>
          <div className="bg-amber-50 p-4 rounded-sm">
            <MessageSquare className="h-6 w-6 text-amber-600 mb-2" />
            <p className="font-semibold text-[#1B2A4A]">Viva</p>
            <p className="text-sm text-slate-600">12% weightage</p>
          </div>
          <div className="bg-indigo-50 p-4 rounded-sm">
            <Shield className="h-6 w-6 text-indigo-600 mb-2" />
            <p className="font-semibold text-[#1B2A4A]">Ethics</p>
            <p className="text-sm text-slate-600">8% weightage</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Visual Weightage Chart */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Component Weightages</h3>
        <div className="flex flex-col gap-3">
          {config.components?.map((comp, idx) => {
            const Icon = componentIcons[comp.component] || FileText;
            const colors = componentColors[comp.component] || { bg: 'bg-slate-100', text: 'text-slate-700' };
            
            return (
              <div key={idx} className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${colors.bg}`}>
                  <Icon className={`h-5 w-5 ${colors.text}`} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-[#1B2A4A]">
                      {componentNames[comp.component]}
                      {comp.is_safety_critical && (
                        <Badge className="ml-2 text-[10px] bg-red-100 text-red-700">Safety Critical</Badge>
                      )}
                    </span>
                    <span className="text-sm font-bold text-[#1B2A4A]">{comp.weightage}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${colors.bg.replace('100', '500')}`}
                      style={{ width: `${comp.weightage}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Passing threshold: {comp.passing_threshold}%
                    {comp.min_cases && ` • Min cases: ${comp.min_cases}`}
                    {comp.min_questions && ` • Min questions: ${comp.min_questions}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Award Classification */}
      <div className="bg-gradient-to-r from-[#1B2A4A] to-[#2d3f5e] rounded-sm p-6 text-white">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Star className="h-5 w-5 mr-2 text-[#C5A572]" /> Award Classification
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-sm p-4 text-center">
            <p className="text-3xl font-bold text-[#C5A572]">{config.overall_passing_threshold}-{config.merit_threshold - 1}%</p>
            <p className="text-sm mt-1">Certified</p>
          </div>
          <div className="bg-white/10 rounded-sm p-4 text-center">
            <p className="text-3xl font-bold text-[#C5A572]">{config.merit_threshold}-{config.distinction_threshold - 1}%</p>
            <p className="text-sm mt-1">Certified with Merit</p>
          </div>
          <div className="bg-white/10 rounded-sm p-4 text-center">
            <p className="text-3xl font-bold text-[#C5A572]">{config.distinction_threshold}%+</p>
            <p className="text-sm mt-1">Certified with Distinction</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Rubric Section Component
function RubricSection({ title, icon: Icon, children, color }) {
  return (
    <AccordionItem value={title} className="border border-slate-200 rounded-sm mb-3">
      <AccordionTrigger className="px-4 py-3 hover:bg-slate-50">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="font-semibold text-[#1B2A4A]">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

// Default exam configurations (fallback if API returns empty)
const defaultConfigs = [
  {
    id: 'default-cbeam',
    examination_code: 'CBEAM',
    examination_name: 'Competency-Based Examination in Aesthetic Medicine',
    level: 'board_certification',
    components: [
      { component: 'mcq', weightage: 35, passing_threshold: 70, is_safety_critical: true, min_questions: 150 },
      { component: 'case_log', weightage: 20, passing_threshold: 60, is_safety_critical: false, min_cases: 30 },
      { component: 'osce', weightage: 25, passing_threshold: 60, is_safety_critical: true },
      { component: 'viva', weightage: 12, passing_threshold: 60, is_safety_critical: false },
      { component: 'ethics', weightage: 8, passing_threshold: 60, is_safety_critical: true }
    ],
    overall_passing_threshold: 70,
    merit_threshold: 80,
    distinction_threshold: 90,
    active: true
  },
  {
    id: 'default-cbelm',
    examination_code: 'CBELM',
    examination_name: 'Competency-Based Examination in Longevity Medicine',
    level: 'board_certification',
    components: [
      { component: 'mcq', weightage: 35, passing_threshold: 70, is_safety_critical: true, min_questions: 120 },
      { component: 'case_log', weightage: 20, passing_threshold: 60, is_safety_critical: false, min_cases: 25 },
      { component: 'osce', weightage: 25, passing_threshold: 60, is_safety_critical: true },
      { component: 'viva', weightage: 12, passing_threshold: 60, is_safety_critical: false },
      { component: 'ethics', weightage: 8, passing_threshold: 60, is_safety_critical: true }
    ],
    overall_passing_threshold: 70,
    merit_threshold: 80,
    distinction_threshold: 90,
    active: true
  },
  {
    id: 'default-cberm',
    examination_code: 'CBERM',
    examination_name: 'Competency-Based Examination in Regenerative Medicine',
    level: 'board_certification',
    components: [
      { component: 'mcq', weightage: 35, passing_threshold: 70, is_safety_critical: true, min_questions: 120 },
      { component: 'case_log', weightage: 20, passing_threshold: 60, is_safety_critical: false, min_cases: 20 },
      { component: 'osce', weightage: 25, passing_threshold: 60, is_safety_critical: true },
      { component: 'viva', weightage: 12, passing_threshold: 60, is_safety_critical: false },
      { component: 'ethics', weightage: 8, passing_threshold: 60, is_safety_critical: true }
    ],
    overall_passing_threshold: 70,
    merit_threshold: 80,
    distinction_threshold: 90,
    active: true
  }
];

export default function ExaminationStructurePage() {
  const [configs, setConfigs] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API}/exam-configurations`);
      const data = res.data && res.data.length > 0 ? res.data : defaultConfigs;
      setConfigs(data);
      if (data.length > 0) {
        setSelectedExam(data[0]);
      }
    } catch (err) {
      console.error(err);
      // Use default configs on error
      setConfigs(defaultConfigs);
      setSelectedExam(defaultConfigs[0]);
    } finally {
      setLoading(false);
    }
  };

  const levelLabels = {
    assistant: 'Assistant Level',
    board_certification: 'Board Certification',
    fellowship: 'Fellowship Program'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading examination structure...</p>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="examination-structure-page">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-12">
        <div className="imcac-container">
          <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Assessment Framework</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-heading">
            Universal Examination Structure
          </h1>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">
            IMCAC employs a standardized 5-component evaluation model ensuring comprehensive 
            assessment of clinical competency, safety awareness, and ethical practice.
          </p>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Examination Selector */}
        <Card className="imcac-card mb-8">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Select Examination</h3>
            <div className="flex flex-wrap gap-2">
              {configs.map((config) => (
                <button
                  key={config.examination_code}
                  onClick={() => setSelectedExam(config)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    selectedExam?.examination_code === config.examination_code
                      ? 'bg-[#1B2A4A] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {config.examination_code}
                  <Badge className="ml-2 text-[10px]" variant="outline">
                    {levelLabels[config.level] || config.level}
                  </Badge>
                </button>
              ))}
            </div>
            {selectedExam && (
              <p className="text-sm text-slate-600 mt-3">
                <strong>{selectedExam.examination_name}</strong>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue="infographic" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-sm">
            <TabsTrigger value="infographic" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Target className="h-4 w-4 mr-2" /> Scoring Overview
            </TabsTrigger>
            <TabsTrigger value="rubrics" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" /> Detailed Rubrics
            </TabsTrigger>
            <TabsTrigger value="governance" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Shield className="h-4 w-4 mr-2" /> Quality Assurance
            </TabsTrigger>
          </TabsList>

          {/* Scoring Infographic Tab */}
          <TabsContent value="infographic">
            <ScoringInfographic config={selectedExam} />
          </TabsContent>

          {/* Detailed Rubrics Tab */}
          <TabsContent value="rubrics">
            <Accordion type="single" collapsible className="space-y-2">
              {/* A. Written Examination */}
              <RubricSection title="A. Written Examination (MCQ)" icon={BookOpen} color="bg-blue-100 text-blue-700">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Structure</h4>
                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                      <li>150–200 MCQs (scaled based on certification level)</li>
                      <li>Scenario-based clinical questions</li>
                      <li>Evidence-based approach</li>
                      <li>Computer-based testing option</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Scoring</h4>
                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                      <li>1 mark per question</li>
                      <li>Automatic weighted conversion</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Passing Criteria</h4>
                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                      <li>Minimum 70% overall</li>
                      <li>Mandatory minimum in Complication Management domain</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                    <p className="text-sm text-red-700 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      <strong>Safety Clause:</strong>&nbsp;Failure in safety-critical domain = automatic fail.
                    </p>
                  </div>
                </div>
              </RubricSection>

              {/* B. Case Log / Portfolio */}
              <RubricSection title="B. Case Log / Portfolio Assessment" icon={ClipboardCheck} color="bg-purple-100 text-purple-700">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Requirements by Level</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="font-medium text-[#1B2A4A]">Assistant Level</p>
                        <p className="text-2xl font-bold text-purple-700">15</p>
                        <p className="text-xs text-slate-500">documented cases</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="font-medium text-[#1B2A4A]">Certification Level</p>
                        <p className="text-2xl font-bold text-purple-700">30</p>
                        <p className="text-xs text-slate-500">documented cases</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="font-medium text-[#1B2A4A]">Fellowship Level</p>
                        <p className="text-2xl font-bold text-purple-700">50+</p>
                        <p className="text-xs text-slate-500">advanced cases</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Scoring Rubric (5-Point Anchored Scale)</h4>
                    <div className="grid grid-cols-5 gap-2 text-center text-xs">
                      <div className="bg-red-100 text-red-700 p-2 rounded-sm">
                        <p className="font-bold">1</p>
                        <p>Unsafe</p>
                      </div>
                      <div className="bg-orange-100 text-orange-700 p-2 rounded-sm">
                        <p className="font-bold">2</p>
                        <p>Below Standard</p>
                      </div>
                      <div className="bg-yellow-100 text-yellow-700 p-2 rounded-sm">
                        <p className="font-bold">3</p>
                        <p>Competent</p>
                      </div>
                      <div className="bg-green-100 text-green-700 p-2 rounded-sm">
                        <p className="font-bold">4</p>
                        <p>Independent & Safe</p>
                      </div>
                      <div className="bg-blue-100 text-blue-700 p-2 rounded-sm">
                        <p className="font-bold">5</p>
                        <p>Expert-Level</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Assessment Criteria</h4>
                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                      <li>Documentation quality</li>
                      <li>Clinical reasoning</li>
                      <li>Treatment planning</li>
                      <li>Safety compliance</li>
                      <li>Reflective learning</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                    <p className="text-sm text-red-700 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      <strong>Automatic fail trigger:</strong>&nbsp;Unsafe or unethical documentation.
                    </p>
                  </div>
                </div>
              </RubricSection>

              {/* C. OSCE */}
              <RubricSection title="C. OSCE (Clinical Skills Examination)" icon={Award} color="bg-green-100 text-green-700">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Stations Include</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {['Patient assessment', 'Injection planning', 'Emergency management simulation', 'Consent & communication', 'Complication management'].map((station, idx) => (
                        <div key={idx} className="bg-green-50 p-2 rounded-sm text-sm text-green-700 flex items-center">
                          <CheckCircle className="h-3 w-3 mr-2" /> {station}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Scoring Scale</h4>
                    <div className="grid grid-cols-5 gap-2 text-center text-xs">
                      <div className="bg-red-100 text-red-700 p-2 rounded-sm">
                        <p className="font-bold">1</p>
                        <p>Unsafe</p>
                      </div>
                      <div className="bg-orange-100 text-orange-700 p-2 rounded-sm">
                        <p className="font-bold">2</p>
                        <p>Below Standard</p>
                      </div>
                      <div className="bg-yellow-100 text-yellow-700 p-2 rounded-sm">
                        <p className="font-bold">3</p>
                        <p>Competent w/ supervision</p>
                      </div>
                      <div className="bg-green-100 text-green-700 p-2 rounded-sm">
                        <p className="font-bold">4</p>
                        <p>Independent & safe</p>
                      </div>
                      <div className="bg-blue-100 text-blue-700 p-2 rounded-sm">
                        <p className="font-bold">5</p>
                        <p>Advanced proficiency</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                    <h4 className="font-semibold text-amber-800 mb-1">Rules</h4>
                    <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                      <li>No score of 1 allowed</li>
                      <li>Mandatory pass in emergency station</li>
                    </ul>
                  </div>
                </div>
              </RubricSection>

              {/* D. Viva Voce */}
              <RubricSection title="D. Viva Voce & Case Defense" icon={MessageSquare} color="bg-amber-100 text-amber-700">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Panel Format</h4>
                    <p className="text-sm text-slate-600">Minimum 2 examiners</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Evaluation Areas</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {['Clinical judgment', 'Risk mitigation', 'Evidence-based decision-making', 'Treatment justification'].map((area, idx) => (
                        <div key={idx} className="bg-amber-50 p-2 rounded-sm text-sm text-amber-700 flex items-center">
                          <CheckCircle className="h-3 w-3 mr-2" /> {area}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                    <p className="text-sm text-green-700">
                      <strong>Passing:</strong> Minimum 60%
                    </p>
                  </div>
                </div>
              </RubricSection>

              {/* E. Ethics & Governance */}
              <RubricSection title="E. Ethics & Governance Assessment" icon={Shield} color="bg-indigo-100 text-indigo-700">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A] mb-2">Assessment Format</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {['Scenario-based questions', 'Regulatory awareness', 'Scope of practice knowledge', 'Advertising & documentation ethics'].map((item, idx) => (
                        <div key={idx} className="bg-indigo-50 p-2 rounded-sm text-sm text-indigo-700 flex items-center">
                          <CheckCircle className="h-3 w-3 mr-2" /> {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                    <p className="text-sm text-red-700 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      <strong>Automatic fail:</strong>&nbsp;Unethical justification or unsafe scope practice.
                    </p>
                  </div>
                </div>
              </RubricSection>
            </Accordion>
          </TabsContent>

          {/* Quality Assurance Tab */}
          <TabsContent value="governance">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Governance & Quality Control */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-[#C5A572]" /> Governance & Quality Control
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Double marking system for all assessments',
                      'Examiner calibration meetings',
                      'External moderation policy',
                      'Standardized marking rubrics',
                      'Annual curriculum review'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Appeals & Re-examination */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-[#C5A572]" /> Appeals & Re-examination
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Formal appeals procedure within 30 days',
                      'Independent review panel for disputes',
                      'Re-examination eligibility after 3 months',
                      'Maximum 3 examination attempts',
                      'Remediation support available'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Certification Validity */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4 flex items-center">
                    <Award className="h-5 w-5 mr-2 text-[#C5A572]" /> Certification Validity
                  </h3>
                  <ul className="space-y-3">
                    {[
                      '5-year certification validity period',
                      'Annual CPD requirements (minimum 20 hours)',
                      'Recertification pathway available',
                      'Digital verification system',
                      'International recognition framework'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Security & Compliance */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-[#C5A572]" /> Security & Compliance
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Data encryption at rest and in transit',
                      'Secure login authentication',
                      'Complete audit trail logs',
                      'Role-based examiner access',
                      'GDPR-compliant data handling'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
